import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoreBar } from '@/components/ui/ScoreBar';
import { EmptyState } from '@/components/feedback/EmptyState';
import { FieldStatus } from './FieldStatus';

export interface CategoryField {
  fieldName: string;
  status: 'complete' | 'missing';
  packagingLayer?: string;
}

export interface CategoryDetail {
  categoryId: string;
  categoryName: string;
  score: number;
  applicable?: boolean;
  fields: CategoryField[];
}

interface CategoryBreakdownProps {
  categories: CategoryDetail[];
  activeCategoryId?: string | null;
}

const CATEGORY_ICONS: Record<string, string> = {
  label: 'description',
  photos: 'photo_camera',
  packaging: 'inventory_2',
  hazardous: 'warning',
  marketing: 'campaign',
};

export function CategoryBreakdown({ categories, activeCategoryId }: CategoryBreakdownProps) {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (activeCategoryId) initial.add(activeCategoryId);
    return initial;
  });

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!categories.length) return null;

  return (
    <div data-testid="category-breakdown" className="space-y-3">
      {categories.map((cat) => {
        const isExpanded = expandedIds.has(cat.categoryId);
        const isActive = cat.categoryId === activeCategoryId;
        const icon = CATEGORY_ICONS[cat.categoryId] ?? 'category';

        return (
          <div
            key={cat.categoryId}
            data-testid={`category-section-${cat.categoryId}`}
            data-state={isExpanded ? 'open' : 'closed'}
            className="rounded-xl overflow-hidden"
            style={{
              backgroundColor: 'var(--background)',
              border: '1px solid var(--border-soft)',
              borderLeftWidth: isActive ? '3px' : '1px',
              borderLeftColor: isActive ? 'var(--secondary)' : 'var(--border-soft)',
              boxShadow: 'var(--card-shadow)',
              opacity: cat.applicable === false ? 0.5 : 1,
            }}
          >
            {/* Section header — clickable */}
            <button
              type="button"
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
              onClick={() => toggle(cat.categoryId)}
              aria-expanded={isExpanded}
              aria-controls={`category-content-${cat.categoryId}`}
            >
              <span
                className="material-symbols-outlined shrink-0"
                style={{ fontSize: '20px', color: 'var(--primary)' }}
                aria-hidden="true"
              >
                {icon}
              </span>

              <span
                className="text-[0.8125rem] font-semibold flex-1 text-left"
                style={{ color: 'var(--foreground-heading)' }}
              >
                {cat.categoryName}
              </span>

              {cat.applicable === false ? (
                <span className="text-xs font-medium w-32 text-right" style={{ color: '#94A3B8' }}>
                  {t('categories.notApplicable')}
                </span>
              ) : (
                <ScoreBar score={cat.score} variant="compact" className="w-32" />
              )}

              <span
                className="material-symbols-outlined shrink-0 transition-transform duration-200"
                style={{
                  fontSize: '20px',
                  color: 'var(--foreground-muted)',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                aria-hidden="true"
              >
                expand_more
              </span>
            </button>

            {/* Collapsible content */}
            {isExpanded && (
              <div
                id={`category-content-${cat.categoryId}`}
                className="px-4 pb-4"
                style={{ borderTop: '1px solid var(--border-soft)' }}
              >
                {cat.score === 100 ? (
                  <div data-testid="category-complete-state">
                  <EmptyState
                    variant="success"
                    title={t('dashboard.allComplete', { category: cat.categoryName.toLowerCase() })}
                    className="mt-3"
                  />
                  </div>
                ) : (
                  <div className="mt-2 divide-y" style={{ borderColor: 'var(--border-soft)' }}>
                    {cat.fields.map((field, idx) => (
                      <FieldStatus
                        key={`${field.fieldName}-${field.packagingLayer ?? idx}`}
                        fieldName={field.fieldName}
                        isComplete={field.status === 'complete'}
                        packagingLayer={field.packagingLayer}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CategoryBreakdownSkeleton() {
  return (
    <div className="space-y-3" data-testid="category-breakdown-skeleton">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl px-4 py-3.5"
          style={{
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border-soft)',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 rounded animate-pulse" style={{ backgroundColor: 'var(--border)' }} />
            <div className="h-4 flex-1 rounded animate-pulse" style={{ backgroundColor: 'var(--border)' }} />
            <div className="h-2 w-32 rounded animate-pulse" style={{ backgroundColor: 'var(--border)' }} />
          </div>
        </div>
      ))}
    </div>
  );
}
